/**
 * dtos.js
 * 
 * Data Transfer Objects (DTOs) for backend API communication.
 * Transforms form data into backend-expected format.
 * 
 * Exports:
 * - createElementDto: Create element DTO from form values
 * - createMeasureDto: Create measure DTO from form values
 * - createDefectDto: Create defect DTO from form values
 */

import { calulateTotal } from './calculations';

/**
 * Create Element DTO for backend API
 * Handles both old form (elementName object) and new form (elementType string) structures
 * 
 * @param {Object} values - Form values
 * @returns {Object} - Element DTO
 */
export const createElementDto = (values) => {
  // Handle both old form (elementName object) and new form (elementType string) structures
  const isOthersElement = values.isElementOther;
  
  let elementValue = '';
  let parentIdent = '';
  
  if (isOthersElement) {
    // For "Others" elements:
    // elementValue should be "Others"/"Sonstiges" (the dropdown value)
    // customElement contains the user's custom text
    elementValue = values.customElement?.value || values.elementName || values.elementType || '';
    parentIdent = values.customElement?.parentIdent || values.elementName?.parentIdent || values.subCategory?.ident || '';
    
  } else {
    // For regular elements
    if (values.elementName && typeof values.elementName === 'object') {
      // Old form structure - elementName is an object with actual value
      elementValue = values.elementName.value || '';
      // 🔧 FIX: For new form, correct parentIdent is stored in customElement.parentIdent
      parentIdent = values.customElement?.parentIdent || values.elementName.parentIdent || '';
    } else {
      // New form structure - elementType is a string
      elementValue = values.elementType || values.elementName || '';
      // 🔧 FIX: Prioritize customElement.parentIdent (where new form stores it) over others
      parentIdent = values.customElement?.parentIdent || values.elementName?.parentIdent || values.subCategory?.ident || '';
    }
  }

  const customElementTitle = isOthersElement ? (values.customElement?.title || values.customElement?.value || '') : '';

  // 🔧 FIX: For "Others" elements, use customElement.value for trade/name (not "Sonstiges")
  // This matches stage-deployment behavior
  const finalTradeValue = isOthersElement ? customElementTitle : elementValue;
  const finalNameValue = isOthersElement ? customElementTitle : elementValue;

  // 🔧 FIX: Backend rejects empty string for subCategorySecondaryIdent
  // Convert empty string to null or throw error if missing
  const finalParentIdent = parentIdent || null;
  
  if (!finalParentIdent) {
    //console.error('❌ ERROR: subCategorySecondaryIdent is missing! This will cause 702 error.');
  }

  const el = {
    type: "elementDto",
    trade: finalTradeValue,
    name: finalNameValue,
    elementName: isOthersElement
        ? [
            {
              type: "title",
              title: customElementTitle,
              language: "de",
              value: customElementTitle,
            },
            {
              type: "title",
              title: customElementTitle,
              language: "en",
              value: customElementTitle,
            },
          ]
        : null,
    constructionYear: !!values.constructionYear ? values.constructionYear : null,
    subCategorySecondaryIdent: finalParentIdent,
  };
  
  return el;
};

/**
 * Create Measure DTO for backend API
 * 
 * @param {Object} values - Form values
 * @returns {Object} - Measure DTO
 */
export const createMeasureDto = (values) => {
  // Handle both priority (old form) and priorityType (new form) fields
  const priority = values.priority || values.priorityType || "kurzfristig";
  
  return {
    type: "measureDto",
    measureDescription: values.measureDescription,
    pricePerUnit: values.pricePerUnit,
    quantity: Number(values.quantity) || 1, // 🔧 FIX: Convert string to number (backend requirement)
    priority: priority,
    percentageShortTermPriority: values.percentageShortTermPriority || 0,
    percentageMidTermPriority: values.percentageMidTermPriority || 0,
    percentageLongTermPriority: values.percentageLongTermPriority || 0,
    extraCostComment: values.extraCostComment || values.costComment || "",
    extraCost: values.extraCost,
    elementAffected: values.elementAffected || 0,
    probabilityOfOccurrence: values.probabilityOfOccurrence || 0,
    comments: values.comments || "",
    unit: values.unit,
    active: true,
    totalCost: calulateTotal(values, false),
    code: values.costParameters?.code,
    costElement: values.costParameters?.name,
  };
};

/**
 * Create Defect DTO for backend API
 * 
 * @param {Object} values - Form values
 * @returns {Object} - Defect DTO
 */
export const createDefectDto = (values) => {
  return {
    type: "defectDto",
    risk: values.riskType,
    inRedFlag: values.inRedFlag,
    defect: values.defectDescription,
    defectType: values.defectType,
    costDefect: values.costDefect,
    dataRoomIndex: values.dataRoomIndex, // Maps to "Data room Index" field
    quelle: values.quelle,
    questionAndAnswer: values.questionAndAnswer || values.qaField, // Support both old and new field names
    comments: values.comments, // New comments field
    code: null,
  };
};
